import { ArrowRightOutlined, EditOutlined } from "@ant-design/icons";
import { Button, Space, Typography } from "antd";
import Table, { ColumnType } from "antd/lib/table";
import { encode } from "querystring";
import { FunctionComponent, useContext, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useStateIfMounted } from "use-state-if-mounted";
import ActionBtn from "../../../shared/action-btn";
import {
  ROUTE_DASHBOARD_GEAR_DETAIL,
  ROUTE_DASHBOARD_GEAR_EDIT,
} from "../../../shared/app-constants";
import DeleteBtn from "../../../shared/delete-btn";
import GearModel from "../../../shared/gear-model";
import getDashboardRoute from "../../../shared/get-dashboard-route";
import GuardedElement from "../../../shared/guarded-element";
import Permissions from "../../../shared/permissions";
import uDayjs from "../../../shared/uDayjs";
import GearFilter from "../../components/gear-filter";
import { AppContext } from "../../contexts/app-context";
import HttpClient from "../../http-client";
interface GearListPageProps {
  self?: boolean;
}

const GearListPage: FunctionComponent<GearListPageProps> = ({ self }) => {
  const [gears, setGears] = useStateIfMounted<GearModel[]>([]);
  const [busy, setBusy] = useStateIfMounted(false);
  const [hasMore, setHasMore] = useStateIfMounted(false);
  const { user } = useContext(AppContext);
  const [search] = useSearchParams();
  const loadMore = useRef<() => Promise<void>>();

  const remove = (id: string) => {
    setGears((list) =>
      list.filter((each) => {
        return each.id !== id;
      })
    );
  };
  const columns: ColumnType<any>[] = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (val, record) => (
        <Link
          to={{
            pathname: getDashboardRoute(
              { id: record.id },
              ROUTE_DASHBOARD_GEAR_DETAIL
            ),
          }}
        >
          {val}
        </Link>
      ),
    },
    {
      title: "Urls",
      dataIndex: "urls",
      key: "urls",
      render: (urls: any[]) =>
        urls
          ?.map(({ root_url, contains }) => {
            const tmp: string[] = [];
            if (root_url) {
              tmp.push(root_url);
            }
            if (contains) {
              tmp.push(contains);
            }
            return tmp.join(":");
          })
          ?.join(", "),
    },
    {
      title: "Created at",
      dataIndex: "created_at",
      key: "created_at",
      render: (value) =>
        value ? uDayjs(value).format("DD/MM/YYYY hh:mma") : null,
    },
    {
      title: "Created by",
      dataIndex: "created_by",
      key: "created_by",
      render: (value) => (value ? `${value.name}` : null),
    },
    {
      title: "Updated at",
      dataIndex: "updated_at",
      key: "updated_at",
      render: (value) =>
        value ? uDayjs(value).format("DD/MM/YYYY hh:mma") : null,
    },
    {
      title: "Updated by",
      dataIndex: "updated_by",
      key: "updated_by",
      render: (value) => (value ? `${value.name}` : null),
    },
    {
      title: "Actions",
      key: "id",
      dataIndex: "id",
      render: (id, gear) => (
        <Space>
          <Button type="default" size="small" icon={<ArrowRightOutlined />}>
            <Link
              to={{
                pathname: getDashboardRoute(
                  { id },
                  ROUTE_DASHBOARD_GEAR_DETAIL
                ),
              }}
            >
              Detail
            </Link>
          </Button>
          <GuardedElement permissions={[Permissions.EDIT_GEAR]}>
            <Button type="default" size="small" icon={<EditOutlined />}>
              <Link
                to={{
                  pathname: getDashboardRoute(
                    { id },
                    ROUTE_DASHBOARD_GEAR_EDIT
                  ),
                }}
              >
                Edit
              </Link>
            </Button>
          </GuardedElement>
          <GuardedElement permissions={[Permissions.DELETE_GEAR]}>
            <DeleteBtn
              path={`gear/${id}`}
              httpClient={HttpClient}
              onDone={() => remove(id)}
            />
          </GuardedElement>
        </Space>
      ),
    },
  ];

  useEffect(() => {
    setGears([]);
    let page = 0;
    loadMore.current = async () => {
      page++;
      setBusy(true);
      try {
        const query: any = { page };
        if (self) {
          query.self = true;
        }
        if (search.get("from")) {
          query.from = uDayjs(search.get("from")).utc().format();
        }
        if (search.get("to")) {
          query.to = uDayjs(search.get("to")).utc().format();
        }
        if (search.get("name")) {
          query.name = search.get("name");
        }
        if (search.get("url")) {
          query.url = search.get("url");
        }
        if (search.get("tags")) {
          query.tags = decodeURIComponent(search.get("tags")!)
            .split(",")
            .map((tag) => tag.split("|"))
            .map(([id]) => id);
        }
        const qs = encode(query);
        const { data } = await HttpClient.get(`gear?${qs}`);
        if (Array.isArray(data?.data)) {
          setGears((list) => [...list, ...data.data]);
        }
        setHasMore(!!data?.meta?.nextPage);
      } catch (e) {
        console.log(e);
      }

      setBusy(false);
    };
    loadMore.current();
  }, [search, self, user]);
  return (
    <Space direction="vertical">
      <Typography.Title level={4}>Gears</Typography.Title>
      <GearFilter />
      <Table
        columns={columns}
        loading={busy}
        dataSource={gears}
        rowKey={"id"}
        pagination={false}
        footer={() => {
          if (hasMore) {
            return (
              <div className="tc">
                <ActionBtn busy={busy} onClick={loadMore.current}>
                  Load More
                </ActionBtn>
              </div>
            );
          }
          return null;
        }}
      />
    </Space>
  );
};

export default GearListPage;
